import React, { useMemo, useState } from 'react';
import './StatsStrip.css';
import { todayPuzzleId, isSamePuzzleId } from '../utils/puzzleId';
import storage from '../utils/storage';

// Local copy: SummaryBackside and this file format ordinals slightly
// differently (suffix-only vs. number+suffix). Consolidating is Phase 4 work,
// when the summary screen gets broken up.
const ordinal = (n) => {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
};

/**
 * One-line strip above the grid.
 *
 * This carries the retention beat that used to sit inside the instructions
 * modal ("Yesterday you finished 4th of 60"). That beat was worth keeping, but
 * it was behind a wall every returning player had to tap through; here they see
 * it in half a second. The row also owns the persistent "?" that reopens the
 * how-to-play, since the modal no longer opens on its own.
 *
 * Deliberately at most one line: `.App` is a fixed-height, non-scrolling
 * column, so anything added here is taken from the grid.
 */
const StatsStrip = ({
  userPuzzleHistory, todayPuzzlePerformances, onShowInstructions, showHelp = true,
  archiveDate = null, onBackToToday,
}) => {
  const todayId = todayPuzzleId();

  const [dismissed, setDismissed] = useState(
    () => isSamePuzzleId(storage.get(storage.KEYS.STATS_STRIP_DISMISSED), todayId)
  );

  const message = useMemo(() => {
    // Highest priority: they've already played today and are on the board.
    if (Array.isArray(todayPuzzlePerformances)) {
      const index = todayPuzzlePerformances.findIndex(entry => entry.is_you);
      if (index !== -1) {
        return (
          <>
            You&apos;ve already played today — currently{' '}
            <strong>{ordinal(index + 1)}</strong> of {todayPuzzlePerformances.length}
          </>
        );
      }
    }

    // Otherwise, how they did yesterday.
    const yesterday = userPuzzleHistory?.yesterdayPuzzleHistory;
    if (Array.isArray(yesterday) && yesterday.length > 0) {
      const index = yesterday.findIndex(entry => entry.is_you);
      if (index !== -1) {
        return (
          <>
            Yesterday you finished <strong>{ordinal(index + 1)}</strong> of {yesterday.length}
          </>
        );
      }
    }

    return null;
  }, [userPuzzleHistory, todayPuzzlePerformances]);

  const handleDismiss = () => {
    storage.set(storage.KEYS.STATS_STRIP_DISMISSED, todayId);
    setDismissed(true);
  };

  // In archive mode the strip's job is different: say which puzzle this is and
  // offer the way back, rather than report today's standing.
  if (archiveDate) {
    return (
      <div className="stats-strip">
        <span className="stats-strip-text">
          Playing the archive — <strong>{archiveDate}</strong>
        </span>
        <button type="button" className="stats-strip-back" onClick={onBackToToday}>
          Back to today
        </button>
      </div>
    );
  }

  const showMessage = message && !dismissed;

  // Nothing to say and no affordance to offer: take up no vertical space.
  // The grid is the thing competing for it.
  if (!showMessage && !showHelp) return null;

  return (
    <div className={`stats-strip ${showMessage ? '' : 'stats-strip-bare'}`}>
      {showMessage ? (
        <>
          <span className="stats-strip-text">{message}</span>
          <button
            type="button"
            className="stats-strip-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            ×
          </button>
        </>
      ) : (
        <span className="stats-strip-text" />
      )}
      {showHelp && (
        <button
          type="button"
          className="stats-strip-help"
          onClick={onShowInstructions}
          aria-label="How to play"
          title="How to play"
        >
          ?
        </button>
      )}
    </div>
  );
};

export default StatsStrip;
